// Logo - the Foresee brand mark, shared with the marketing site.
//
// Per the brand sheet the dial instrument IS the O of the wordmark: "the O of
// the wordmark, the app icon, and the favicon are the same instrument at
// different scales". It is drawn as SVG rather than shipped as a PNG so it
// stays crisp at every size, inherits the surface's text colour for its
// contrast ring, and can actually sweep.
//
// Ported from ForeseeWebsite/src/landing-front/src/components/Logo.tsx -
// same geometry, same "Aurora ice" palette, same 9s cadence, so the panel and
// the site are recognisably one product.

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/classnames';

// Dial colours resolve through --logo-* (index.css). The dish is dark so its
// interior separates from light surfaces; the rim is currentColor so it
// contrasts on dark ones.
const DIAL = {
  dish: 'var(--logo-dish)',
  wedge1: 'var(--logo-wedge1)',
  wedge2: 'var(--logo-wedge2)',
  range: 'var(--logo-range)',
  needle: 'var(--logo-needle)',
  halo: 'var(--logo-halo)',
  blip: 'var(--logo-blip)',
} as const;

export function LogoMark({
  size = 32,
  className,
  glow = false,
  /** Live radar: continuous sweep with a contact blip that fades as the
   *  needle passes it and reappears somewhere new on each pass. Respects
   *  prefers-reduced-motion. */
  animated = false,
  /** One-shot sweep while hovered - an interaction rather than an
   *  autoplaying loop, which is what dense chrome wants. */
  hoverSweep = false,
  /** Decoration only (no img role) - used inside the lockup, where the
   *  letters already carry the accessible name. */
  decorative = false,
}: {
  size?: number;
  className?: string;
  glow?: boolean;
  animated?: boolean;
  hoverSweep?: boolean;
  decorative?: boolean;
}) {
  // The brand sheet drops detail below 48px, but the O in the wordmark should
  // read as the full instrument, so full detail is kept down to 20px. Below
  // that only ring, needle and blip render.
  const detail = size >= 20;

  const sweepRef = useRef<SVGGElement>(null);
  const contactRef = useRef<SVGGElement>(null);

  // Live-radar loop: the needle rotates at the brand's 9s cadence; the
  // contact's opacity decays with the needle's angular distance past it
  // (freshly painted → fading echo), and while the contact is nearly dark the
  // blip relocates so the next pass "finds" something new.
  useEffect(() => {
    const sweep = sweepRef.current;
    const contact = contactRef.current;
    if (!animated || !sweep || !contact) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const NEEDLE_BASE = -45; // needle's resting direction (45° up-right)
    const SWEEP_SECONDS = 9;
    let theta = 225;
    let radius = 8.98;
    let relocated = false;
    let ang = 0;
    let lastT: number | null = null;
    let raf = 0;

    const place = () => {
      theta = Math.random() * 360;
      radius = 4 + Math.random() * 8;
      const rad = (theta * Math.PI) / 180;
      const x = (20 + radius * Math.cos(rad)).toFixed(2);
      const y = (20 + radius * Math.sin(rad)).toFixed(2);
      contact.setAttribute('transform', `translate(${x} ${y})`);
    };

    const frame = (t: number) => {
      if (lastT === null) lastT = t;
      // Clamp the frame delta: rAF pauses in background tabs, and an
      // unclamped dt on return would snap the needle mid-sweep.
      const dt = Math.min((t - lastT) / 1000, 0.05);
      lastT = t;
      ang = (ang + (dt * 360) / SWEEP_SECONDS) % 360;
      sweep.setAttribute('transform', `rotate(${ang.toFixed(2)} 20 20)`);
      const needleDir = (((NEEDLE_BASE + ang) % 360) + 360) % 360;
      const delta = (((needleDir - theta) % 360) + 360) % 360;
      const opacity = Math.max(0.05, Math.min(1, 1 - (delta / 360) * 1.25));
      contact.setAttribute('opacity', opacity.toFixed(2));
      if (delta > 250 && delta < 330 && !relocated) {
        place();
        relocated = true;
      }
      if (delta < 20) relocated = false;
      raf = requestAnimationFrame(frame);
    };

    place();
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [animated]);

  return (
    <svg
      width={size}
      height={size}
      /* Tight viewBox framing the ring (r17 + stroke) rather than the full
         40-unit canvas, so the visible O fills its box and its centre
         coincides with the ring centre. The needle still rotates about
         (20,20), which stays valid. */
      viewBox="1.5 1.5 37 37"
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : 'Foresee'}
      aria-hidden={decorative || undefined}
      className={cn(
        'block shrink-0 select-none',
        glow && 'drop-shadow-[0_0_22px_rgba(56,189,248,0.45)]',
        hoverSweep && 'logo-hover-sweep',
        className,
      )}
    >
      {/* Dish - the instrument face. */}
      <circle cx="20" cy="20" r="17" fill={DIAL.dish} />
      {/* Range ring - hairline at 58% radius. A 1-unit stroke is a true
          hairline at hero scale but falls below one device pixel on small
          marks, so it thickens as the mark shrinks. */}
      {detail && (
        <circle
          cx="20"
          cy="20"
          r="9.8"
          fill="none"
          stroke={DIAL.range}
          strokeWidth={Math.min(1.8, Math.max(1, 37 / size)).toFixed(2)}
        />
      )}
      {/* Sweep trail (two flat wedges 50° behind the needle) and the needle. */}
      <g ref={sweepRef} className="logo-sweeper">
        <path d="M20 20 L18.68 4.86 A15.2 15.2 0 0 1 30.75 9.25 Z" fill={DIAL.wedge1} />
        {detail && (
          <path d="M20 20 L26.42 6.23 A15.2 15.2 0 0 1 30.75 9.25 Z" fill={DIAL.wedge2} />
        )}
        <line
          x1="20"
          y1="20"
          x2="31.38"
          y2="8.62"
          stroke={DIAL.needle}
          strokeWidth={detail ? 2.2 : 2.6}
          strokeLinecap="round"
        />
      </g>
      {/* Contact - blip on its halo. Grouped so the loop can translate and
          fade it as one unit. */}
      <g ref={contactRef} transform="translate(13.65 13.65)">
        {detail && <circle r="3.4" fill={DIAL.halo} />}
        <circle r={detail ? 1.9 : 2.2} fill={DIAL.blip} />
      </g>
      {/* Hub - pivot dot at centre. */}
      {detail && <circle cx="20" cy="20" r="1.3" fill={DIAL.needle} />}
      {/* Contrast ring - currentColor, so it inherits the surface's text
          colour, which every surface guarantees legible against itself. That
          satisfies the brand rule (light ring on dark, ink ring on light)
          automatically. */}
      <circle
        cx="20"
        cy="20"
        r="17"
        fill="none"
        stroke="currentColor"
        strokeWidth={detail ? 2.6 : 3}
      />
    </svg>
  );
}

/** F + dial + RESEE as one word, not a badge beside text. */
export function LogoLockup({
  size = 18,
  className,
  markClassName,
  glow = false,
  animated = false,
  hoverSweep = false,
}: {
  /** Wordmark font size in px; the dial (the O) scales with it. */
  size?: number;
  className?: string;
  markClassName?: string;
  glow?: boolean;
  animated?: boolean;
  hoverSweep?: boolean;
}) {
  return (
    <span
      role="img"
      aria-label="FORESEE"
      className={cn(
        'inline-flex items-baseline font-semibold uppercase leading-none tracking-[0.01em]',
        className,
      )}
      style={{ fontSize: size }}
    >
      <span aria-hidden>F</span>
      {/* At 0.86em the dial slightly overshoots the cap height for a
          confident letterform O, nudged down so its centre lands on the cap
          band's midline. */}
      <LogoMark
        decorative
        size={Math.round(size * 0.86)}
        glow={glow}
        animated={animated}
        hoverSweep={hoverSweep}
        className={cn('mx-[0.06em] translate-y-[0.08em]', markClassName)}
      />
      <span aria-hidden>RESEE</span>
    </span>
  );
}
